// src/features/medicalSercices/page/serviceItemForm.tsx

import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';
import { useRouter } from '@tanstack/react-router';
import { CheckCategoryItemCode } from '../../../../common/services/realTimeCheckAPI';
import { useTranslation } from 'react-i18next';

export default function ServiceItemForm() {
    const { t } = useTranslation();

    const { state } = useRouter().state.location;
    const record = (state as { record?: Record<string, unknown> })?.record;
    const id = record && record.id !== undefined && record.id !== null
        ? Number(record.id)
        : undefined;

    const serviceItemFormSchema: DynamicFormSchema = {
        layout: 'vertical',
        showResetButton: true,

        sections: [
            {
                key: 'serviceItem-details',
                title: 'Category Service Item Form',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [

                    {
                        name: 'name',
                        label: 'Name',
                        type: 'input',
                        required: true,
                        requiredMessage: t('Please enter a name.'),
                        validation: [
                            { min: 3, message: t('Name must be at least 3 characters long.') },
                            { max: 100, message: t('Name cannot be more than 100 characters long.') },
                        ],
                        autocomplete: 'false',
                        colSpan: 12,
                    },

                    {
                        name: 'code',
                        label: 'Code',
                        type: 'input',
                        required: true,
                        requiredMessage: t('Please enter a unique code.'),
                        autocomplete: 'false',
                        pattern: 'AlphaNumeric',
                        validation: [
                            { min: 1, message: t('Code must be at least 1 characters long.') },
                            { max: 10, message: t('Code cannot be more than 10 characters long.') },

                            // ✅ NO SPACES ALLOWED (ONLY ADDITION)
                            {
                                pattern: /^\S+$/,
                                message: t('Spaces are not allowed in code.'),
                            },

                            {
                                validator: async (_: any, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 1) return Promise.resolve();

                                    try {
                                        const result = await CheckCategoryItemCode(value, id);
                                        if (result.exists) {
                                            return Promise.reject(t('Code already exists.'));
                                        }
                                        return Promise.resolve();
                                    } catch (error: any) {
                                        console.error(t('Error validating Code:'), error);
                                        return Promise.reject(error.message || t('Error validating Code.'));
                                    }
                                },
                            },
                        ],
                        colSpan: 12,
                    },

                    {
                        name: 'serviceCategoryId',
                        label: 'Category',
                        type: 'select',
                        required: true,
                        requiredMessage: t('Select category.'),
                        placeholder: "Select action",
                        options: [],
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { "dropdowns": ["allservicecategories"] },
                            transformData: "(apiResponse) => apiResponse.allservicecategories.map(item => ({ label: item.Name, value: item.Id }))"
                        },
                        colSpan: 12,
                    },

                    {
                        name: 'description',
                        label: 'Description',
                        type: 'input',
                        required: true,
                        requiredMessage: t('Please enter a service serviceItem description.'),
                        autocomplete: 'false',
                        validation: [
                            { max: 500, message: t('Description cannot be more than 500 characters long.') },
                        ],
                        colSpan: 24,
                    },

                    {
                        name: 'isActive',
                        label: 'Status',
                        type: 'switch',
                        colSpan: 12,
                        defaultValue: true,
                        hidden: true
                    },
                ],
            },
        ],
    };

    return (
        <DynamicForm
            schema={serviceItemFormSchema}
            submitApiUrl={API_ENDPOINTS.MEDICALSERVICES.SAVE_SERVICECATEGORYITEM}
            getByIdApiUrl={API_ENDPOINTS.MEDICALSERVICES.GET_SERVICECATEGORYITEM_BY_ID}
            updateApiUrl={API_ENDPOINTS.MEDICALSERVICES.UPDATE_SERVICECATEGORYITEM}
            refreshTableDataKey={QUERY_KEYS.MEDICALSERVICES.SERVICECATEGORYITEMS}
        />
    );
}